/**
 * i18n Viewer (design 09b) — lớp APP giữ `locale` bằng rune `$state` (engine ở shared là thuần).
 * Gọi `t()`/`tn()` trong template/`$derived` đọc rune → tự cập nhật khi đổi ngôn ngữ.
 *
 * Thứ tự ưu tiên:
 *   1. `?lang=` — tác giả gửi link đúng tiếng khách (không ghi localStorage, không bị IP ghi đè).
 *   2. Đã cài đặt — localStorage (LANG_STORAGE_KEY, DÙNG CHUNG với Console → đồng bộ).
 *   3. Mặc định theo IP (Cloudflare `/cdn-cgi/trace`): VN → vi, còn lại → en. Logic chung ở shared.
 * Trong lúc chờ IP dùng mặc định tạm theo navigator (vi/en) để giảm "nháy".
 */
import {
  translate,
  plural,
  fmtDate as fDate,
  fmtNum as fNum,
  asLocale,
  provisionalLocale,
  geoDefaultLocale,
  LANG_STORAGE_KEY,
  LOCALES,
  LANG_NAMES,
} from '@wood-viewer/shared';
import type { Locale } from '@wood-viewer/shared';
import { VIEWER_MESSAGES } from './messages';

type Params = Record<string, string | number>;

function urlLang(): Locale | null {
  try {
    return asLocale(new URLSearchParams(location.search).get('lang'));
  } catch {
    return null;
  }
}

function storedLocale(): Locale | null {
  try {
    return asLocale(localStorage.getItem(LANG_STORAGE_KEY));
  } catch {
    return null;
  }
}

// true = đã có nguồn xác định (?lang hoặc đã cài đặt) → KHÔNG để IP ghi đè.
let fixed = false;

function readInitial(): Locale {
  const url = urlLang();
  if (url) {
    fixed = true; // ?lang: ưu tiên cao nhất, không persist
    return url;
  }
  const stored = storedLocale();
  if (stored) {
    fixed = true;
    return stored;
  }
  return provisionalLocale(); // chưa cài đặt → mặc định tạm, chờ IP
}

let locale = $state<Locale>(readInitial());

export function getLocale(): Locale {
  return locale;
}

export function setLocale(l: Locale): void {
  fixed = true; // user chủ động chọn → IP không ghi đè nữa
  locale = l;
  try {
    localStorage.setItem(LANG_STORAGE_KEY, l);
  } catch {
    /* localStorage không khả dụng */
  }
}

/** Khi chưa có ?lang và chưa cài đặt: đặt mặc định theo IP (VN → vi, còn lại → en). Không persist. */
async function resolveDefaultByIp(): Promise<void> {
  if (fixed) return;
  const byIp = await geoDefaultLocale();
  if (byIp && !fixed && !storedLocale()) locale = byIp;
}

void resolveDefaultByIp();

export function t(key: string, params?: Params): string {
  return translate(VIEWER_MESSAGES, locale, key, params);
}

export function tn(key: string, n: number, params?: Params): string {
  return plural(VIEWER_MESSAGES, locale, key, n, params);
}

export function fmtNum(n: number, opts?: Intl.NumberFormatOptions): string {
  return fNum(locale, n, opts);
}

export function fmtDate(d: string | number | Date, opts?: Intl.DateTimeFormatOptions): string {
  return fDate(locale, d, opts);
}

export { LOCALES, LANG_NAMES };
export type { Locale };
